import React, { useRef, useState, useEffect } from 'react';
import { FormInputDropdown } from 'app/components/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDropdown';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  reimbursementDescriptionId,
  reimbursementFrequency,
  reimbursementFrequencyId,
  reimbursementCategoryId,
  ApprovalStatus,
} from 'app/models/Placement/PlacementMods';
import {
  isCategoryDescriptionFrequencyDefaultDaily,
  isCategoryDescriptionFrequencyDefaultPerShift,
  isMiscellaneousCellPhoneStipend,
} from './ReimbursementHelper';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { useSelector } from 'react-redux';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { selectCurrentModDetails } from 'store/redux-store/placement-mods/selectors';
import * as StatusDefaults from 'app/components/Placement/NextPlacementStatus/StatusDefaults';

export enum FrequencyType {
  OneTimeOnly = 1,
  Weekly = 2,
  Monthly = 3,
  Annually = 4,
  Daily = 5,
  PerIncident = 6,
  PerShift = 7,
}

export const Frequency = ({ options, isModEdit, useDefaultFrequencyValues, onDescriptionChange }: { 
  options: any; 
  isModEdit: boolean; 
  useDefaultFrequencyValues: boolean;
  onDescriptionChange?: (isDirty: boolean) => void;
}) => {
  const { control, setValue, clearErrors, getValues, setError } = useFormContext();
  const reimbursementFrequencyValue = useWatch<any>({ name: reimbursementFrequencyId });
  const reimbursementCategoryIdValue: any = useWatch({ name: reimbursementCategoryId });
  const reimbursementCategoryDescriptionId = useWatch<any>({ name: reimbursementDescriptionId });
  const placementStatus = useSelector(selectPlacementStatus);
  const isActiveStatus = StatusDefaults.isPositiveActiveStatus(placementStatus?.currentPlacementStatus);
  const frequencyOptions = options?.find(item => parseInt(item.ID) === reimbursementCategoryIdValue)?.frequencies;
  let initialState = true;
  const initialDescriptionRef = useRef<any>(null);
  const [isDescriptionDirty, setIsDescriptionDirty] = useState(false);
  const currentModDetails = useSelector(selectCurrentModDetails);
  const isApproved = currentModDetails?.approvalStatusId === ApprovalStatus.Approved;

  const handleValueChanged = value => {
    setValue(reimbursementFrequency, value);
    const selectedValue = frequencyOptions?.find(item => item.Description === value);
    setValue(reimbursementFrequencyId, selectedValue?.ID ? parseInt(selectedValue?.ID) : '');
    if (value) {
      clearErrors(reimbursementFrequency);
      clearErrors('initialFrequency');
    }
    initialState = false;
  };

  useEffect(() => {
    const isFirstRender = initialDescriptionRef.current === null;
    const hasDescriptionChanged = !isFirstRender && 
      initialDescriptionRef.current !== reimbursementCategoryDescriptionId;
    
    if (hasDescriptionChanged) {
      setIsDescriptionDirty(true);
      // Notify parent component if callback is provided
      onDescriptionChange?.(true);
    }
    
    if (isMiscellaneousCellPhoneStipend(reimbursementCategoryIdValue, reimbursementCategoryDescriptionId) && 
        isActiveStatus && isModEdit) {
          if (reimbursementFrequencyValue === null || reimbursementFrequencyValue === undefined || 
            isApproved || hasDescriptionChanged) {
            setValue(reimbursementFrequencyId, FrequencyType.PerShift, { shouldDirty: true });
          }
    }
    else if (useDefaultFrequencyValues && isModEdit) {
      // Set frequency if:
      // 1. Description has changed (user changed it), OR
      // 2. Frequency is not set and description matches a default frequency type
      const frequencyNotSet = isNullOrUndefined(reimbursementFrequencyValue);
      const shouldSetDefaultFrequency = hasDescriptionChanged || frequencyNotSet;
      
      if (shouldSetDefaultFrequency) {   
        if (isCategoryDescriptionFrequencyDefaultPerShift(reimbursementCategoryDescriptionId)) {
          setValue(reimbursementFrequencyId, FrequencyType.PerShift);
        } else if (isCategoryDescriptionFrequencyDefaultDaily(reimbursementCategoryDescriptionId)) {
          setValue(reimbursementFrequencyId, FrequencyType.Daily);
        }
      }    
    }
    initialDescriptionRef.current = reimbursementCategoryDescriptionId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reimbursementCategoryDescriptionId]);

  useEffect(() => {
    const value = frequencyOptions?.find(item => parseInt(item.ID) === reimbursementFrequencyValue)?.Description;
    setValue(reimbursementFrequency, value ?? null);
    if (isModEdit && isNullOrUndefined(value) && initialState) {
      setError('initialFrequency', {});
    } else {
      clearErrors('initialFrequency');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequencyOptions, getValues(reimbursementFrequencyId)]);

  return (
    <>
      <Controller
        control={control}
        name={reimbursementFrequencyId}
        rules={{ required: isModEdit }}
        render={({ field: { onChange, ...rest } }) => {
          return (
            <FormInputDropdown
              readOnly={!isModEdit}
              name={reimbursementFrequency}
              label={`Frequency *`}
              require={isModEdit}
              standard={true}
              options={frequencyOptions ?? []}
              inputValue={getValues(reimbursementFrequency) ?? null}
              handleChange={e => {
                onChange(e);
                handleValueChanged(e.target.value);
              }}
            />
          );
        }}
      />
    </>
  );
};
